import type { ChecklistCorner, ChecklistStatus, DriverRuleField, DriverRuleParams } from '@mt/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { prepareReportPhoto } from '@/lib/report-photo';
import { FLEET_KEY } from './api';

/**
 * PR5 Quy tắc tài xế mới (docs/phases/P21.md §9.8, DEC-200…209): checklist nhận xe, quên trả xe, lệnh giao sau 17h,
 * tham số quy tắc, duyệt tiền tài xế tháng.
 */
export const RULES_KEY = [...FLEET_KEY, 'driver-rules'] as const;

export type RulePhotoKind = 'vehicle' | 'checklist' | 'slip' | 'overnight' | 'crane' | 'task' | 'weekly';
export async function uploadRulePhoto(kind: RulePhotoKind, file: File) {
  const { data, thumb, lat, lng, sourceSha256 } = await prepareReportPhoto(file);
  return api.post<{ id: number }>('/fleet/driver-rules/photos', { kind, data, thumb, lat, lng, sourceSha256 });
}

export interface ChecklistItem {
  id: number;
  label: string;
  craneOnly: boolean;
  severe: boolean;
  sortOrder: number;
  isActive: boolean;
  version: number;
}
export interface ChecklistState {
  receiveId: number | null;
  vehicleId: number | null;
  plateNo: string | null;
  isCrane: boolean;
  required: boolean;
  checklistId: number | null;
  status: ChecklistStatus | null;
  locked: boolean;
  lockReason: string | null;
}
export type AutoReturnStatus = 'pending' | 'confirmed' | 'fix_requested' | 'fixed' | 'fix_rejected';
export const AUTO_RETURN_STATUS_LABELS: Record<AutoReturnStatus, string> = {
  pending: 'Chờ tài xế xác nhận',
  confirmed: 'Tài xế đã xác nhận',
  fix_requested: 'Xin sửa giờ, chờ duyệt',
  fixed: 'Đã sửa giờ',
  fix_rejected: 'Không cho sửa giờ',
};
export interface AutoReturn {
  id: number;
  driverId: number;
  driverName: string;
  vehicleId: number;
  plateNo: string;
  workDate: string;
  receivedAt: string;
  returnedAt: string;
  gpsBased: boolean;
  status: AutoReturnStatus;
  requestedTime: string | null;
  requestReason: string | null;
  decidedByName: string | null;
  decideNote: string | null;
  nightMoved: boolean | null;
  morningOdometerKm: number | null;
  /** auto = hệ thống tự trả; late_return = tài xế tự trả sau giờ trả xe tối đa (DEC-616) */
  source: 'auto' | 'late_return';
}
export interface DriverMe {
  driverId: number;
  checklist: ChecklistState;
  items: ChecklistItem[];
  autoReturns: AutoReturn[];
  provinceTrips: ProvinceTrip[];
  month: { month: string; orders: number; km: number; pickups: number; pickupsDone: number; nights: number };
}
export const useDriverMe = () => useQuery({ queryKey: [...RULES_KEY, 'me'], queryFn: () => api.get<DriverMe>('/fleet/driver-rules/me') });

export type CornerPhotos = Record<ChecklistCorner, number | null>;
export interface Checklist {
  id: number;
  handoverId: number;
  vehicleId: number;
  plateNo: string;
  driverId: number;
  driverName: string;
  kind: 'receive' | 'return';
  status: ChecklistStatus;
  results: { itemId: number; label: string; severe: boolean; ok: boolean; photoId: number | null; note: string | null }[];
  photos: CornerPhotos;
  handoverAt: string;
  odometerKm: number;
  odometerPhotoId: number | null;
  createdAt: string;
  unlockedByName: string | null;
  unlockedAt: string | null;
  unlockReason: string | null;
}
export interface RepairTask {
  id: number;
  vehicleId: number;
  plateNo: string;
  title: string;
  note: string | null;
  photoId: number | null;
  severe: boolean;
  status: 'open' | 'done';
  createdAt: string;
  doneAt: string | null;
  doneNote: string | null;
}
export interface ChecklistList {
  checklists: Checklist[];
  lockedVehicles: { vehicleId: number; plateNo: string; lockedAt: string; reason: string; checklistId: number | null }[];
  repairTasks: RepairTask[];
}
export const useChecklists = (f: { vehicleId?: number | null; status?: string; from?: string; to?: string }) =>
  useQuery({
    queryKey: [...RULES_KEY, 'checklists', f],
    queryFn: () => api.get<ChecklistList>('/fleet/checklists', { vehicleId: f.vehicleId ?? undefined, status: f.status || undefined, from: f.from || undefined, to: f.to || undefined }),
  });
export const useChecklistItems = () => useQuery({ queryKey: [...RULES_KEY, 'items'], queryFn: () => api.get<ChecklistItem[]>('/fleet/checklist-items') });
export const useChecklistCompare = (id: number | null) =>
  useQuery({
    queryKey: [...RULES_KEY, 'compare', id],
    queryFn: () =>
      api.get<{ current: Checklist; previousReturn: { handoverId: number; at: string; odometerKm: number; odometerPhotoId: number | null; driverName: string; photos: CornerPhotos } | null }>(
        `/fleet/checklists/${id}/compare`,
      ),
    enabled: !!id,
  });
export const useAutoReturns = (month: string) => useQuery({ queryKey: [...RULES_KEY, 'auto-returns', month], queryFn: () => api.get<AutoReturn[]>('/fleet/auto-returns', { month }) });

// --- Lệnh giao sau 17h ---
export interface After17Order {
  id: number;
  code: string;
  status: string;
  orderCode: string;
  customerCode: string | null;
  customerName: string;
  salesName: string | null;
  appointment: string | null;
  reason: string;
  backfill: boolean;
  byName: string | null;
  plateNo: string | null;
  driverName: string | null;
  deliveredAt: string | null;
  gpsOk: boolean | null;
  decision: 'ok' | 'rejected' | null;
  decisionNote: string | null;
  decidedByName: string | null;
}
export interface After17Excess {
  employeeId: number;
  fullName: string;
  date: string;
  plateNo: string | null;
  checkOut: string | null;
  maxReturnAt: string | null;
  excessMinutes: number;
  approved: boolean;
  reason: string | null;
}
export interface After17Month {
  month: string;
  confirmation: { confirmedAt: string; confirmedByName: string | null; note: string | null } | null;
  orders: After17Order[];
  excess: After17Excess[];
}
export const useAfter17Month = (month: string) => useQuery({ queryKey: [...RULES_KEY, 'after17', month], queryFn: () => api.get<After17Month>('/fleet/after17', { month }) });
export interface After17Group {
  key: string;
  label: string;
  orders: number;
  rejected: number;
  amount: number | null;
}
export const useAfter17Report = (month: string, enabled = true) =>
  useQuery({
    queryKey: [...RULES_KEY, 'after17-report', month],
    queryFn: () => api.get<{ month: string; showMoney: boolean; bySales: After17Group[]; byCustomer: After17Group[]; total: { orders: number; amount: number | null } }>('/fleet/after17/report', { month }),
    enabled,
  });

// --- Tham số quy tắc + duyệt tiền tháng ---
export interface RuleVersion {
  id: number;
  effectiveFrom: string;
  params: DriverRuleParams;
  note: string | null;
  createdAt: string;
  createdByName: string | null;
  editable: boolean;
}
export const useRuleParams = () =>
  useQuery({ queryKey: [...RULES_KEY, 'params'], queryFn: () => api.get<{ today: string; current: DriverRuleParams; fields: DriverRuleField[]; versions: RuleVersion[] }>('/fleet/driver-rules/params') });
export interface PayMonthStatus {
  month: string;
  approved: boolean;
  approvedAt?: string;
  approvedByName?: string | null;
  note?: string | null;
}
export const usePayMonth = (month: string) => useQuery({ queryKey: [...RULES_KEY, 'pay-month', month], queryFn: () => api.get<PayMonthStatus>(`/hr/driver-pay-months/${month}`) });

export const minutesLabel = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} giờ${m % 60 ? ` ${m % 60} phút` : ''}` : `${m} phút`);

// --- Chuyến tỉnh (4.7) ---
export type ProvinceStatus = 'planned' | 'running' | 'returned' | 'approved' | 'rejected' | 'cancelled';
export const PROVINCE_STATUS_LABELS: Record<ProvinceStatus, string> = {
  planned: 'Chưa đi',
  running: 'Đang đi',
  returned: 'Đã về, chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Không duyệt',
  cancelled: 'Đã hủy',
};
type LatLng = { lat: number; lng: number } | null;
export interface ProvinceTrip {
  id: number;
  destination: string;
  plannedDays: number;
  plannedStart: string;
  vehicleId: number;
  plateNo: string;
  driverId: number;
  driverName: string;
  assistantDriverId: number | null;
  assistantName: string | null;
  status: ProvinceStatus;
  startedAt: string | null;
  arrivedAt: string | null;
  returnedAt: string | null;
  gps: { start: LatLng; arrive: LatLng; return: LatLng };
  nights: { date: string; at: string; lat: number | null; lng: number | null }[];
  overdue: boolean;
  overdueConfirmed: boolean;
  overdueReason: string | null;
  overdueByName: string | null;
  pay: { days: number; nights: number; perDiem: number; lodging: number; amount: number; sundays: string[]; overPlanned: boolean; dates: string[]; assistantAmount?: number } | null;
  payApproved: boolean;
  payMonth: string | null;
  note: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  rejectReason: string | null;
  cancelReason: string | null;
  version: number;
  createdByName: string | null;
  createdAt: string;
}
export const useProvinceTrips = (month: string) => useQuery({ queryKey: [...RULES_KEY, 'province', month], queryFn: () => api.get<ProvinceTrip[]>('/fleet/province-trips', { month }) });

// --- Đơn & km tài xế tháng (4.9) ---
export interface ReportCounts {
  orders: number;
  points: number;
  after17: number;
  pickups: number;
  province: number;
  nights: number;
  flags: number;
  waitMinutes: number | null;
  gpsKm: number | null;
  odoKm: number | null;
  contractKm: number | null;
  kmMismatch: boolean;
  kmPerOrder: number | null;
}
export interface DriverReportRow extends ReportCounts {
  driverId: number;
  fullName: string;
  days: { date: string; orders: number; gpsKm: number }[];
}
export interface VehicleReportRow extends ReportCounts {
  vehicleId: number;
  plateNo: string;
}
export const useDriverReport = (month: string) =>
  useQuery({ queryKey: [...RULES_KEY, 'report', month], queryFn: () => api.get<{ month: string; drivers: DriverReportRow[]; vehicles: VehicleReportRow[] }>('/fleet/driver-report', { month }) });
